import validator from 'validator';
import {isEmpty} from 'lodash';
import StabAddressFormConfigurator from '@homeaway/stab-address-form-configurator';
import {getBillingSubmitErrorAttempt} from './localStorageDataHandler';
import {BILLING_ERROR_MAX_ATTEMPT, QUEBEC_COUNTRY_CODE, QUEBEC_STATE_CODE, PHONE_DENY_LIST} from '../utils/constants';

const validateEmail = (email) => {
    return validator.isEmail(email);
};

const validatePassword = (password) => {
    return (/^(?=.*[0-9])(?=.*[a-zA-Z])(.+){7,}$/).test(password);
};

const validatePhoneNumber = (phoneNumber, countryCode) => {
    
    if (!phoneNumber) {
        return false;
    }

    const strippedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');

    const numberToCheck = countryCode + strippedPhoneNumber;

    if (PHONE_DENY_LIST.includes(numberToCheck)) {
        return false;
    }

    const phoneRegex = /^[\d\-\s\(\)\+]+$/;

    return (phoneRegex).test(phoneNumber);
};

const validatePropertiesCount = (count) => {
    return (/^[1-9]\d*$/).test(count);
};

const validateCountOfProperty = (count) => {
    return (/^[0-9]*$/).test(count);
};

const isUsSite = (siteName) => (siteName === 'homeaway_us' || siteName === 'vrbo' || siteName === 'vr');

const isSpainSite = (siteName) => (siteName === 'homeaway_es');

const isUkSite = (siteName) => (siteName === 'homeaway_uk');

const isPortugueseSite = (siteName) => (siteName === 'homeaway_pt');

const isNetNewIPM = (userType) => (userType === 'IPM')

const isPPM = (userType) => (userType === 'PPM');

const isSWSwitchIPM = (userType) => (userType === 'IPM_SOFTWARE_SWITCH');

const isEMEABrandSite = (siteName) => (siteName === 'homeaway_uk' || siteName === 'abritel' || siteName === 'homeaway_de' || siteName === 'homeaway_it' || siteName === 'homeaway_pt' || siteName === 'homeaway_es');

const dsaBrandSite = ['abritel', 'homeaway_de', 'homeaway_it', 'homeaway_pt', 'homeaway_es'];

const isDSABrandSite = (siteName) => (dsaBrandSite.includes(siteName));

const isAuSite = (siteName) => (siteName === 'homeaway_au');

const isNzSite = (siteName) => (siteName === 'homeaway_nz');

const isStateInAddress = (country) => {
    const addressForm = StabAddressFormConfigurator.getConfigByCountry(country);
    return addressForm.hasOwnProperty('stateProvince');
};

const checkCreateAccountBtnStatus = (state) => {
    return !isEmpty(state) && state.firstName && state.lastName && state.phoneNumber
        && state.companyName && state.email && state.password
        && !(state.firstNameError || state.lastNameError
        || state.phoneNumberError || state.companyNameError
        || state.emailError || state.passwordError);
};

const checkCreateWebformContinueBtnStatus = (state) => {
    return !isEmpty(state) && state.emailAddress && state.firstName && state.lastName && state.phoneNumber
        && state.numberOfProperty && state.companyName
        && !(state.emailError || state.firstNameError || state.lastNameError
        || state.phoneNumberError || state.companyNameError
        || state.propertyCountError);
};

const isBillingErrorMaxAttemptReached = () => (parseInt(getBillingSubmitErrorAttempt(), 10) === BILLING_ERROR_MAX_ATTEMPT);

const checkBillingInfoBtnStatus = (state) => {
    let isValid = false;
    if (!isEmpty(state) && state.cardName && state.expireMonth && state.expireYear
        && state.billingCountry && state.billingAddressLine1 && state.billingCity
        && state.billingPostalCode && state.isPanValid && state.isCvnValid
        && !(state.cardNameError || state.expireMonthError || state.expireYearError || state.panErrorClassName || state.cvnErrorClassName)
        && !isBillingErrorMaxAttemptReached()) {
        isValid = (isStateInAddress(state.billingCountry)) ? !!(state.billingState) : true;
    }

    return isValid;
};

const checkingVatInfoBtnStatus = (state) => {
    let isValid = false;
    if (!isEmpty(state) && state.vatInformationCountry && state.vatNumber
        && state.vatAddressCountry && state.vatAddressLine1 && state.vatCity && state.vatPostalCode) {
        isValid = (isStateInAddress(state.vatAddressCountry)) ? !!(state.vatState || state.vatStateSelect) : true;
    }

    return isValid;
};

const checkCompanyLocBtnStatus = (state) => {
    return !isEmpty(state) && state.companyName && state.companyCountryCode
        && (!(state.registrationNumberChoice === 'Yes') || state.registrationNumber)
        && (!state.showBusinessType || state.businessType);
};

const checkBasicInfoBtnStatus = (state) => {
    return !isEmpty(state) && state.firstName && state.lastName && state.email
        && !(state.firstNameError || state.lastNameError
        || state.emailError);
};

const checkSoftwareDetailsBtnStatus = (state) => {
    return !isEmpty(state) && state.softwareName && state.softwareId && state.systemId
        && !(state.softwareNameError || state.softwareIdError);
};

const checkMailingAddressBtnStatus = (state) => {
    let isValid = false;
    if (!isEmpty(state) && state.mailingCountry && state.mailingAddressLine1 && state.mailingCity && state.mailingPostalCode
        && state.billingCountry && state.billingAddressLine1 && state.billingCity && state.billingPostalCode) {
        if (isStateInAddress(state.mailingCountry)) {
            isValid = !!state.mailingState;
        } else if (isStateInAddress(state.billingCountry)) {
            isValid = !!state.billingState;
        } else {
            isValid = true;
        }
    }

    return isValid;
};

// eslint-disable-next-line complexity
const checkReviewModalBtnStatus = (state) => {
    let isValid = false;

    if (state.enableVatModal) {
        if (state.showRegistrationNumber && state.vatChoice === 'No') {
            isValid = !!(state.vatCountryCode && state.registrationNumber);
        } else if (!isEmpty(state) && state.addressCountry && state.vatCountryCode && state.vatNumber
            && state.addressLine1 && state.city && state.postalCode) {
            isValid = (isStateInAddress(state.addressCountry)) ? !!(state.state) : true;
        }
    } else if (state.enableAccountModal && !isEmpty(state) && state.firstName && state.lastName && state.dialingCountryCode && state.phoneNumber
        && state.addressCountry && state.companyName.trim().length > 0  && state.companyCountryCode && state.addressLine1 && state.city
        && state.postalCode && state.billingCountry && state.billingAddressLine1 && state.billingCity && state.billingPostalCode) {

        if (isStateInAddress(state.addressCountry) && isStateInAddress(state.billingCountry)) {
            isValid = !!state.state && !!state.billingState;
        } else {
            isValid = true;
        }
    }

    return isValid;
};

const checkReviewVatEditBtnStatus = (state) => {
    let isValid = false;
    if (state.enableAccountModal && !isEmpty(state) && state.firstName && state.lastName && state.dialingCountryCode && state.phoneNumber
        && state.addressCountry && state.companyName && state.companyCountryCode && state.addressLine1 && state.city
        && state.postalCode && state.billingCountry && state.billingAddressLine1 && state.billingCity && state.billingPostalCode) {

        if (isStateInAddress(state.addressCountry) && isStateInAddress(state.billingCountry)) {
            isValid = !!state.state && !!state.billingState;
        } else {
            isValid = true;
        }
    }

    return isValid;
};

const isAddressInBillingInfoAvailable = (billingDetails) => {
    return (billingDetails && billingDetails.billingAccountInfo &&
        (typeof billingDetails.billingAccountInfo.billingAccountAddresses !== 'undefined' &&
            billingDetails.billingAccountInfo.billingAccountAddresses.length > 0));
};

const isAddressFromQuebec = (address) =>
    !!(address && address.country && address.stateProvince && address.country === QUEBEC_COUNTRY_CODE &&
        address.stateProvince === QUEBEC_STATE_CODE);

const vatCountries = ['AT', 'EE', 'FI', 'GR', 'IT', 'LV', 'IL', 'SM'];

const excludeTaxIdentifier = (value) => {
    return vatCountries.includes(value);
}

const validTaxIdentifierCountries = ['FR', 'DE', 'PT', 'ES', 'GB'];

const isValidTaxIdentifierCountry = (value) => {
    return validTaxIdentifierCountries.includes(value);
}

export {
    validateEmail,
    validatePassword,
    validatePropertiesCount,
    validateCountOfProperty,
    validatePhoneNumber,
    checkCreateAccountBtnStatus,
    checkCreateWebformContinueBtnStatus,
    checkBillingInfoBtnStatus,
    checkCompanyLocBtnStatus,
    isUsSite,
    isSpainSite,
    checkingVatInfoBtnStatus,
    checkBasicInfoBtnStatus,
    checkSoftwareDetailsBtnStatus,
    checkMailingAddressBtnStatus,
    isPortugueseSite,
    isNetNewIPM,
    isPPM,
    isSWSwitchIPM,
    isUkSite,
    checkReviewModalBtnStatus,
    isAddressInBillingInfoAvailable,
    isAddressFromQuebec,
    isBillingErrorMaxAttemptReached,
    isEMEABrandSite,
    isDSABrandSite,
    isAuSite,
    isNzSite,
    excludeTaxIdentifier,
    checkReviewVatEditBtnStatus,
    isValidTaxIdentifierCountry
};
